import React, { useState, useEffect, useMemo, useRef } from "react";
import { Klydo } from "../../../Types";
import DSDialog from "../../system-design/DSDialog";
import DSDialogTitle from "../../system-design/DSDialogTitle";
import DSDialogContent from "../../system-design/DSDialogContent";
import KlydoCard from "../../Gifts/GiftCategories/components/KlydoCard";
import KlydoListCard from "../../Gifts/GiftCategories/components/KlydoListCard";
import DSDialogActions from "../../system-design/DSDialogActions";
import DSSecondaryButton from "../../system-design/DSSecondaryButton";
import DSPrimaryButton from "../../system-design/DSPrimaryButton";
import DSIconButton from "../../system-design/DSIconButton";
import DSGrid from "../../system-design/DSGrid";
import DSBox from "../../system-design/DSBox";
import DSTypography from "../../system-design/DSTypography";
import DSTextField from "../../system-design/DSTextField";
import DSCheckbox from "../../system-design/DSCheckBox";
import { ViewModule, ViewList } from "@mui/icons-material";

const KLYDO_DISPLAY_DURATION = 2500;

interface KlydoPickModalForVideoProps {
  onClose: () => void;
  collectionKlydos: Klydo[];
  onGenerateVideo: (
    selectedKlydos: Klydo[],
    displayDuration?: number,
    attachToProduct?: boolean,
  ) => void;
}

const KlydoPickModalForVideo: React.FC<KlydoPickModalForVideoProps> = ({
  onClose,
  collectionKlydos,
  onGenerateVideo,
}) => {
  const [selected, setSelected] = useState<string[]>([]);
  const [displayDuration, setDisplayDuration] = useState<number>(
    KLYDO_DISPLAY_DURATION / 1000,
  );
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [attachToProduct, setAttachToProduct] = useState(false);

  const initialIds = useMemo(
    () => collectionKlydos.map((k) => k.id),
    [collectionKlydos],
  );
  const initialSelectedRef = useRef<string[]>([]);

  useEffect(() => {
    setSelected(initialIds);
    initialSelectedRef.current = initialIds;
  }, [initialIds]);

  const handleToggleSelection = (id: string) => {
    setSelected((prev) => {
      const index = prev.findIndex((item) => item === id);
      if (index > -1) {
        return prev.filter((item) => item !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  const handleCancel = () => {
    setSelected(initialSelectedRef.current);
    onClose();
  };

  const handleGenerateVideo = (e: React.MouseEvent) => {
    e.preventDefault();
    const selectedKlydos = collectionKlydos.filter((klydo) =>
      selected.includes(klydo.id),
    );
    onGenerateVideo(selectedKlydos, displayDuration * 1000, attachToProduct);
    onClose();
  };

  const selectAll = () => {
    setSelected(initialIds);
  };

  const deselectAll = () => {
    setSelected([]);
  };

  return (
    <DSDialog open={true} onClose={handleCancel} fullWidth maxWidth="md">
      <DSDialogTitle>
        Select Klydos for Video Generation
        <DSBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mt: 2 }}
        >
          <DSTypography variant="body2">
            {selected.length} of {collectionKlydos.length} klydos selected
          </DSTypography>
          <DSBox display="flex" alignItems="center" gap={1}>
            <DSSecondaryButton onClick={selectAll} size="small">
              Select All
            </DSSecondaryButton>
            <DSSecondaryButton onClick={deselectAll} size="small">
              Deselect All
            </DSSecondaryButton>

            <DSBox display="flex" alignItems="center" sx={{ ml: 1 }}>
              <DSIconButton
                onClick={() => setViewMode("grid")}
                color={viewMode === "grid" ? "primary" : "default"}
                size="small"
              >
                <ViewModule />
              </DSIconButton>
              <DSIconButton
                onClick={() => setViewMode("list")}
                color={viewMode === "list" ? "primary" : "default"}
                size="small"
              >
                <ViewList />
              </DSIconButton>
            </DSBox>
          </DSBox>
        </DSBox>
      </DSDialogTitle>
      <DSDialogContent dividers style={{ height: "60vh", overflow: "hidden" }}>
        <DSBox style={{ height: "100%", overflow: "auto" }}>
          {viewMode === "grid" ? (
            <DSGrid container spacing={1}>
              {collectionKlydos.map((klydo: Klydo) => (
                <DSGrid item key={klydo?.id}>
                  <KlydoCard
                    klydoId={klydo.id}
                    isSelected={selected.includes(klydo?.id)}
                    onToggleSelection={() => handleToggleSelection(klydo?.id)}
                  />
                </DSGrid>
              ))}
            </DSGrid>
          ) : (
            <DSBox sx={{ padding: 1 }}>
              {collectionKlydos.map((klydo: Klydo) => (
                <KlydoListCard
                  key={klydo?.id}
                  klydo={klydo}
                  isSelected={selected.includes(klydo?.id)}
                  onToggleSelection={() => handleToggleSelection(klydo?.id)}
                />
              ))}
            </DSBox>
          )}
        </DSBox>
      </DSDialogContent>
      <DSDialogActions>
        <DSBox
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            width: "100%",
            justifyContent: "space-between",
          }}
        >
          <DSBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <DSTypography variant="body2">Display Duration:</DSTypography>
            <DSTextField
              type="number"
              value={displayDuration}
              onChange={(e) => {
                const value = parseFloat(e.target.value);
                if (!isNaN(value) && value >= 1 && value <= 30) {
                  setDisplayDuration(value);
                }
              }}
              inputProps={{
                min: 1,
                max: 30,
                step: 0.5,
              }}
              size="small"
              sx={{
                width: 80,
                "& .MuiInputBase-input": {
                  padding: "4px 8px",
                },
              }}
              placeholder="5s"
            />
            <DSTypography variant="body2">seconds</DSTypography>
            <DSBox
              sx={{ display: "flex", alignItems: "center", marginLeft: 2 }}
            >
              <DSCheckbox
                checked={attachToProduct}
                onChange={(e) => setAttachToProduct(e.target.checked)}
                size="small"
              />
              <DSTypography variant="body2">
                Send to Shopify product
              </DSTypography>
            </DSBox>
          </DSBox>
          <DSBox sx={{ display: "flex", gap: 1 }}>
            <DSSecondaryButton onClick={handleCancel}>Cancel</DSSecondaryButton>
            <DSPrimaryButton
              onClick={handleGenerateVideo}
              disabled={selected.length === 0}
            >
              {attachToProduct ? "Generate & Send" : "Generate Video"} (
              {selected.length} klydos)
            </DSPrimaryButton>
          </DSBox>
        </DSBox>
      </DSDialogActions>
    </DSDialog>
  );
};

export default KlydoPickModalForVideo;
